import { t } from "@lingui/macro";
import {
  type GetUsersQuery,
  SortOrder,
  SqlOperator,
  UserOrderByColumnEnum,
  UsersWhereColumn,
} from "@src/__generated__/graphql";
import { ChangeUserPasswordStore } from "@src/components/modules/users/listing/table/change-user-password/store";
import type { UserType } from "@src/stores/models/Me";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { userTypeToScope } from "@src/utils/userTypeToScope";
import { computed, makeObservable, observable } from "mobx";
import { UserModel } from "./models";

export enum QuickFilters {
  All = "all",
  Active = "active",
  Archived = "archived",
}

const QuickFilterDefault = QuickFilters.Active;

/**
 * store holding projects page related data
 */
export class StoreUsers {
  refetch?: () => void;
  pagination = new PaginationState("users");

  @observable searchTerm = "";

  orderBy = new OrderBy<UserOrderByColumnEnum>([
    { column: UserOrderByColumnEnum.FullName, order: SortOrder.Asc },
  ]);

  where = new Filters<UsersWhereColumn>(
    [
      new Filter({
        column: UsersWhereColumn.Status,
        hidden: true,
        operator: SqlOperator.Eq,
        title: "",
        options: [],
        value: QuickFilterDefault,
        skipWhenValueEquals: QuickFilters.All,
      }),
      new Filter({
        column: UsersWhereColumn.RoleId,
        operator: SqlOperator.In,
        title: t`Role`,
        options: [],
      }),
      new Filter({
        column: UsersWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        column: UsersWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Brand`,
        options: [],
      }),
      new Filter({
        column: UsersWhereColumn.Scope,
        operator: SqlOperator.Eq,
        options: [],
        hidden: true,
        value: null,
      }),
    ],
    { onChange: this.pagination.resetPage, orderBy: this.orderBy },
  );

  constructor(public userType: UserType) {
    makeObservable(this);
    this.where.tableKey = `${userType}-users`;
    this.where.filtersByColumn
      .get(UsersWhereColumn.Scope)
      ?.setValue(userTypeToScope(userType));

    switch (userType) {
      case "partner":
        this.where.filtersByColumn.get(UsersWhereColumn.BrandId)?.hide();
        this.where.filtersByColumn.get(UsersWhereColumn.TeamId)?.hide();
        this.where.filtersByColumn.get(UsersWhereColumn.RoleId)?.hide();
        break;
      case "internal":
        this.where.filtersByColumn.get(UsersWhereColumn.BrandId)?.hide();
        break;
      case "client":
        this.where.filtersByColumn.get(UsersWhereColumn.TeamId)?.hide();
        this.where.filtersByColumn.get(UsersWhereColumn.RoleId)?.hide();
        break;
    }
  }

  @computed get activeQuickFilter() {
    return this.where.filtersByColumn.get(UsersWhereColumn.Status);
  }

  @observable.ref users: UserModel[] = [];

  @observable.ref allUserIds: UserModel["id"][] = [];

  /**
   * Store for changing user password
   */
  changeUserPasswordStore = new ChangeUserPasswordStore();

  fromGetUsersQuery(data: GetUsersQuery) {
    this.users = UserModel.fromGetUsersQuery(data.users);
    this.pagination.setFromPaginatorInfo(data.users?.paginatorInfo!);
    this.allUserIds = data.usersSummary.userIds;
    this.where.filtersByColumn.get(UsersWhereColumn.TeamId)?.setOptions(
      data.teamSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );
    this.where.filtersByColumn
      .get(UsersWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brands(data.brandsSimpleMap));
    this.where.filtersByColumn
      .get(UsersWhereColumn.RoleId)
      ?.setOptions(mapToOptions.roles(data.roles));
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get queryParams() {
    return commonQueryVariables(this);
  }

  static create(userType: UserType) {
    return new StoreUsers(userType);
  }
}
